// src/Pages/MyOrders.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthContext";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { Link } from "react-router-dom";
import SEO from "../Components/SEO";

export default function MyOrders() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;

    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, "orders"),
          where("userId", "==", currentUser.uid),
          orderBy("createdAt", "desc")
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setOrders(data);
      } catch (err) {
        console.error("Failed to load orders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentUser]);

  if (!currentUser)
    return (
      <main style={{ padding: 40, textAlign: "center" }}>
        <h2>Please sign in to view your orders</h2>
      </main>
    );

  return (
    <main
      style={{ padding: "40px 20px", maxWidth: 900, margin: "0 auto" }}
      role="main"
    >
      <SEO title="My Orders" description="View your order history." />

      <h1>My Orders</h1>

      {loading && <p style={{ marginTop: 20 }}>Loading your orders...</p>}

      {!loading && orders.length === 0 && (
        <p style={{ marginTop: 20 }}>You have no orders yet.</p>
      )}

      {!loading &&
        orders.map((order) => (
          <div
            key={order.id}
            style={{
              padding: 20,
              marginTop: 18,
              borderRadius: 14,
              border: "1px solid rgba(0,0,0,0.1)",
              background: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px 0",
                fontWeight: 600,
                fontSize: "1.1rem",
              }}
            >
              Order #{order.id.slice(-6)}
            </h3>

            <p style={{ margin: 4 }}>
              <strong>Total:</strong> ${order.total?.toFixed(2)}
            </p>

            <p style={{ margin: 4 }}>
              <strong>Status:</strong>{" "}
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 8,
                  background:
                    order.status === "processing"
                      ? "#fff3cd"
                      : order.status === "shipped"
                      ? "#d1ecf1"
                      : order.status === "delivered"
                      ? "#d4edda"
                      : "#eee",
                  border: "1px solid rgba(0,0,0,0.1)",
                  fontSize: "0.85rem",
                }}
              >
                {order.status}
              </span>
            </p>

            {/* 🔥 TRACKING INFO - Shows ONLY if trackingNumber exists */}
            {order.trackingNumber && (
              <div
                style={{
                  marginTop: 12,
                  marginBottom: 12,
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  borderLeft: "4px solid #007bff",
                  borderRadius: "4px",
                }}
              >
                <p style={{ margin: 0, fontSize: "0.9rem" }}>
                  <strong>Tracking Number:</strong> {order.trackingNumber}
                </p>
                <a
                  href={`https://parcelsapp.com/en/tracking/${order.trackingNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "0.85rem",
                    color: "#007bff",
                    textDecoration: "none",
                    fontWeight: "bold",
                    marginTop: "4px",
                    display: "inline-block",
                  }}
                >
                  Track Package &rarr;
                </a>
              </div>
            )}

            <p style={{ margin: 4 }}>
              <strong>Date:</strong>{" "}
              {order.createdAt?.toDate
                ? order.createdAt.toDate().toLocaleString()
                : "—"}
            </p>

            {/* SHIPPING INFO */}
            {order.shippingInfo && (
              <div style={{ marginTop: 12, fontSize: "0.9rem", color: "#555" }}>
                <p style={{ margin: 2 }}>
                  <strong>Ship to:</strong>
                </p>
                <p style={{ margin: 2 }}>{order.shippingInfo.fullName}</p>
                <p style={{ margin: 2 }}>
                  {order.shippingInfo.address}, {order.shippingInfo.city}
                </p>
                <p style={{ margin: 2 }}>{order.shippingInfo.postalCode}</p>
                {order.shippingInfo.phone && (
                  <p style={{ margin: 2 }}>Phone: {order.shippingInfo.phone}</p>
                )}
              </div>
            )}

            <p style={{ marginTop: 12, fontSize: "0.9rem", color: "#444" }}>
              <strong>Items in this order:</strong>
            </p>

            <ul style={{ marginTop: 4, paddingLeft: 20 }}>
              {order.items?.map((item, i) => (
                <li key={i}>
                  {item.quantity} × {item.name}
                </li>
              ))}
            </ul>

            {/* ⭐ VIEW ORDER BUTTON */}
            <Link
              to={`/order/${order.id}`}
              style={{
                display: "inline-block",
                marginTop: 15,
                padding: "8px 14px",
                background: "#222",
                color: "white",
                borderRadius: 8,
                textDecoration: "none",
                fontSize: "0.9rem",
              }}
            >
              View Order
            </Link>
          </div>
        ))}
    </main>
  );
}